import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../context/NotificationContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import umService from "../../services/university-admin/umService";

const UMClubMembers = ({ club, tenantCtx, refreshClub }) => {
  const { t } = useTranslation(["universityManager", "common"]);
  const { showSuccess, showError } = useNotification();
  const { can } = useUniAdminAuth();
  // club:members is university-wide (not club-scoped); the membership:* role-delegation
  // permissions below are club-scoped, so they always need this club's id.
  const canViewMembers = can(PERMISSIONS.club.members);
  const canAssignRole = can(PERMISSIONS.membership.assignRole, club.id);
  const canRevokeRole = can(PERMISSIONS.membership.revokeRole, club.id);

  const [loading, setLoading] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [userSearch, setUserSearch] = useState("");
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRole, setSelectedRole] = useState("MODERATOR");

  const fetchClubMembers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await umService.getClubMembersDirectory({
        ...tenantCtx,
        clubId: club.id,
        page,
        size: 10,
        search: searchTerm,
      });

      if (response.success && response.data) {
        setMembers(response.data.content || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      } else {
        setMembers([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error("Error fetching club members:", error);
      showError(t("clubs.members.messages.addFailed"));
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [tenantCtx, club.id, page, searchTerm, showError, t]);

  const fetchAvailableUsers = useCallback(async () => {
    try {
      const response = await umService.getPermissionUsers({
        ...tenantCtx,
        page: 0,
        size: 500,
        search: userSearch,
      });

      if (!response.success) {
        setAvailableUsers([]);
        return;
      }

      const existing = new Set(
        (await umService.getClubMembers({ ...tenantCtx, clubId: club.id, page: 0, size: 500, search: "" }))
          .data?.content?.map((item) => item.userId) || []
      );

      setAvailableUsers((response.data.content || []).filter((user) => !existing.has(user.id)));
    } catch (error) {
      console.error("Error loading available users:", error);
      setAvailableUsers([]);
    }
  }, [tenantCtx, club.id, userSearch]);

  useEffect(() => {
    fetchClubMembers();
  }, [fetchClubMembers]);

  useEffect(() => {
    if (!showAddMember) return;
    fetchAvailableUsers();
  }, [showAddMember, fetchAvailableUsers]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleRoleChange = async (member) => {
    if (!newRole) {
      showError(t("clubs.members.selectRole"));
      return;
    }

    try {
      setLoading(true);
      const response = await umService.updateClubMemberRole(
        {
          clubId: club.id,
          userId: member.userId,
          role: newRole,
        },
        tenantCtx
      );

      if (response.success) {
        showSuccess(t("clubs.members.messages.updateSuccess"));
        setEditingMember(null);
        setNewRole("");
        await fetchClubMembers();
        refreshClub();
      } else {
        showError(t("clubs.members.messages.updateFailed"));
      }
    } catch (error) {
      console.error("Error updating member role:", error);
      showError(t("clubs.members.messages.updateFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(t("clubs.members.messages.confirmRemove"))) {
      return;
    }

    try {
      setLoading(true);
      const response = await umService.removeClubMember({ clubId: club.id, userId: member.userId }, tenantCtx);

      if (response.success) {
        showSuccess(t("clubs.members.messages.removeSuccess"));
        await fetchClubMembers();
        refreshClub();
      } else {
        showError(t("clubs.members.messages.removeFailed"));
      }
    } catch (error) {
      console.error("Error removing member:", error);
      showError(t("clubs.members.messages.removeFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUserId) {
      showError(t("clubs.members.selectUser"));
      return;
    }

    try {
      setLoading(true);
      const response = await umService.addClubMember(
        {
          clubId: club.id,
          userId: selectedUserId,
          role: selectedRole,
        },
        tenantCtx
      );

      if (response.success) {
        showSuccess(t("clubs.members.messages.addSuccess"));
        setSelectedUserId("");
        setSelectedRole("MEMBER");
        setShowAddMember(false);
        await fetchClubMembers();
        refreshClub();
      } else {
        showError(t("clubs.members.messages.addFailed"));
      }
    } catch (error) {
      console.error("Error adding member:", error);
      showError(t("clubs.members.messages.addFailed"));
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      ADMIN: { bg: "danger", icon: "fa-crown" },
      MODERATOR: { bg: "warning", icon: "fa-shield-alt" },
      MEMBER: { bg: "primary", icon: "fa-user" },
    };

    const config = roleConfig[role] || roleConfig.MEMBER;

    return (
      <span className={`badge bg-${config.bg} bg-opacity-10 text-${config.bg} fw-bold`}>
        <i className={`fas ${config.icon} me-1`}></i>
        {t(`clubs.members.roles.${role}`, { defaultValue: role })}
      </span>
    );
  };

  // club:members is required to view the general roster.
  if (!canViewMembers) return null;

  return (
    <div className="club-members">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <i className="fas fa-users me-2"></i>
          {t("clubs.members.clubMembersTitle", { count: totalElements })}
        </h5>
        {canAssignRole && (
          <div>
            <button className="btn btn-primary btn-sm me-2" onClick={() => setShowAddMember((prev) => !prev)}>
              <i className={`fas ${showAddMember ? "fa-times" : "fa-user-plus"} me-1`}></i>
              {showAddMember ? t("clubs.members.cancel") : t("clubs.members.addMember")}
            </button>
          </div>
        )}
      </div>

      {showAddMember && canAssignRole && (
        <div className="card border-0 bg-light mb-4">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-5">
                <label className="form-label fw-bold">{t("clubs.members.searchUsers")}</label>
                <input
                  className="form-control"
                  placeholder={t("clubs.members.searchUsersPlaceholder")}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold">{t("clubs.members.user")}</label>
                <select
                  className="form-select"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                >
                  <option value="">{t("clubs.members.selectUser")}</option>
                  {availableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.fullName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label fw-bold">{t("clubs.members.role")}</label>
                <select className="form-select" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                  <option value="ADMIN">{t("clubs.members.roles.ADMIN")}</option>
                  <option value="MODERATOR">{t("clubs.members.roles.MODERATOR")}</option>
                </select>
              </div>
              <div className="col-md-1 d-flex align-items-end">
                <button className="btn btn-success w-100" onClick={handleAddMember} disabled={loading}>
                  <i className="fas fa-check"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4">
        <div className="input-group">
          <span className="input-group-text">
            <i className="fas fa-search"></i>
          </span>
          <input
            type="text"
            className="form-control"
            placeholder={t("clubs.members.searchMembersPlaceholder")}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : members && members.length > 0 ? (
        <>
          <div className="table-responsive">
            <table className="table table-hover align-middle">
              <thead className="bg-light">
                <tr>
                  <th className="fw-bold">{t("clubs.members.columns.member")}</th>
                  <th className="fw-bold">{t("clubs.members.columns.studentNumber")}</th>
                  <th className="fw-bold">{t("clubs.members.columns.role")}</th>
                  <th className="fw-bold text-center">{t("crud.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member) => (
                  <tr key={member.userId}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div
                          className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                          style={{ width: "40px", height: "40px" }}
                        >
                          <i className="fas fa-user text-primary"></i>
                        </div>
                        <div>
                          <div className="fw-bold">{member.fullName}</div>
                          <small className="text-muted">{member.email}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-muted">{member.studentNumber}</span>
                    </td>
                    <td>
                      {editingMember === member.userId ? (
                        <div className="d-flex align-items-center gap-2">
                          <select
                            className="form-select form-select-sm"
                            value={newRole}
                            onChange={(e) => setNewRole(e.target.value)}
                            style={{ width: "120px" }}
                          >
                            <option value="">{t("clubs.members.selectRole")}</option>
                            <option value="ADMIN">{t("clubs.members.roles.ADMIN")}</option>
                            <option value="MODERATOR">{t("clubs.members.roles.MODERATOR")}</option>
                          </select>
                          <button className="btn btn-success btn-sm" onClick={() => handleRoleChange(member)} disabled={loading}>
                            <i className="fas fa-check"></i>
                          </button>
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => {
                              setEditingMember(null);
                              setNewRole("");
                            }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </div>
                      ) : (
                        getRoleBadge(member.role)
                      )}
                    </td>
                    <td className="text-center">
                      {editingMember !== member.userId && (canAssignRole || canRevokeRole) && (
                        <div className="btn-group btn-group-sm">
                          {canAssignRole && (
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => {
                                setEditingMember(member.userId);
                                setNewRole(member.role);
                              }}
                              title={t("clubs.members.changeRoleTitle")}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                          )}
                          {canRevokeRole && (
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => handleRemoveMember(member)}
                              disabled={loading}
                              title={t("clubs.members.removeMemberTitle")}
                            >
                              <i className="fas fa-user-minus"></i>
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div className="text-muted">
                {t("clubs.members.showingMembers", { start: page * 10 + 1, end: Math.min((page + 1) * 10, totalElements), total: totalElements })}
              </div>
              <nav>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${page === 0 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 0}>
                      {t("common:buttons.previous")}
                    </button>
                  </li>
                  {Array.from({ length: totalPages }).slice(Math.max(0, page - 2), Math.max(0, page - 2) + 5).map((_, index) => {
                    const pageNum = Math.max(0, page - 2) + index;
                    if (pageNum >= totalPages) return null;
                    return (
                      <li key={pageNum} className={`page-item ${page === pageNum ? "active" : ""}`}>
                        <button className="page-link" onClick={() => setPage(pageNum)}>
                          {pageNum + 1}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${page === totalPages - 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages - 1}>
                      {t("common:buttons.next")}
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-5">
          <i className="fas fa-users text-muted mb-3" style={{ fontSize: "3rem" }}></i>
          <h6 className="text-muted">{t("clubs.members.noMembersFound")}</h6>
          <p className="text-muted">
            {searchTerm ? t("clubs.members.noMembersMatch") : t("clubs.members.noMembersYet")}
          </p>
        </div>
      )}
    </div>
  );
};

export default UMClubMembers;
